'use client';

import { useSwrWorkplaceActivations, useSwrWorkplaceJobsInfinite } from '@hooks/useSwrApi/endpoints/workplace';
import { WorkplaceJobsListInfinite } from '@components/templates/employer/profile/jobs/partials/WorkplaceJobsInfinite';
import { JobListsContext } from '@components/contexts/JobListsContext';
import { Modal } from '@components/common';
import { JobDetailHub } from '@components/templates/job/JobDetailHub';
import { useMemo, useState } from 'react';
import { useUrlChange } from '@utils/url/useUrlChange';
import { checkSelectedJobSlug } from '@components/templates/employer/profile/jobs/partials/functions';
import { useTranslation } from 'next-i18next';

/** Props */
export interface ActivationsSectionProps {
  slug: string,
  locale: Locale,
  jobsData?: ApiSchema<'JobResult'>,
  topJobsData?: ApiSchema<'JobResult'>,
}

/**
 * @description Workplace Activations Section. This is a **server component**.
 * It renders a section displaying a list of employer activations (active jobs).
 * - `locale` - the current locale
 * - 'slug` - the company slug
 * - `data` - the jobs list data
 */
export const EmployerJobsSection = (props: ActivationsSectionProps) => {
  // Destructure props
  const {
    locale,
    slug,
    jobsData: jobsFromServer,
    topJobsData: topJobsFromServer,
  } = props;

  // Translations
  const { t } = useTranslation('common');

  const {
    data,
    size,
    sizeAsRef,
    setSize,
    isLoading,
    isEmpty,
    isValidating,
  } = useSwrWorkplaceActivations({
    slug,
    locale,
    limit: 10,
    startIndex: 1,
    serverSideData: jobsFromServer,
  });
  const totalPages = data?.at(-1)?.pages || 1;
  const lastLoadedPage = data?.at(-1)?.page || 1;
  const showLoadMore = lastLoadedPage < totalPages;

  // SWR - Jobs
  // ******************************
  const {
    data: topJobsData,
    size: topJobsSize,
    sizeAsRef: topJobsSizeAsRef,
    setSize: topJobsSetSize,
    isLoading: topJobsIsLoading,
    isEmpty: topJobsIsEmpty,
    isValidating: topJobsIsValidating,
  } = useSwrWorkplaceJobsInfinite({
    slug,
    locale,
    limit: 10,
    startIndex: 1,
    serverSideData: topJobsFromServer,
  });
  const topTotalPages = topJobsData?.at(-1)?.pages || 1;
  const topLastLoadedPage = topJobsData?.at(-1)?.page || 1;
  const topJobsShowLoadMore = topLastLoadedPage < topTotalPages;

  // Current list URL
  // *************************************
  const [currentListUrl, setCurrentListUrl] = useState<string | undefined>(undefined);

  const closeJobModal = () => {
    // Use window.history.replaceState to update the URL without adding a new history entry
    window.history.replaceState(null, '', currentListUrl);
  };

  // State to store the selected job slug from the URL
  const [selectedJobSlugQueryParam, setSelectedJobSlug] = useState<string | null>(null);

  // Store data in context
  const jobsListsContext = useMemo(() => ({
    swrJobList: data,
    showLoadMore,
    size,
    setSize,
    isLoading,
  }), [data, size, setSize, showLoadMore, isLoading]);

  // Store data in context
  const topJobListsContext = useMemo(() => ({
    swrJobList: topJobsData,
    topJobsShowLoadMore,
    topJobsSize,
    topJobsSetSize,
    topJobsIsLoading,
  }), [topJobsData, topJobsShowLoadMore, topJobsSize, topJobsSetSize, topJobsIsLoading]);

  // Detect URL changes and check for selectedJobSlug
  useUrlChange((newUrl: string) => {
    const jobSlug = checkSelectedJobSlug(newUrl);
    setSelectedJobSlug(jobSlug); // Update the state with the slug
  });

  // Render component
  // ***********************
  return (
    <>
      {(jobsFromServer?.items && jobsFromServer?.items?.length > 0) && (
        <>
          <h2
            id="workplace-active-jobs"
            className="mb-4 text-xl font-bold"
          >
            {t('menu.jobs')}
          </h2>
          <WorkplaceJobsListInfinite
            locale={locale}
            type="top"
            jobsListData={{
              data,
              size,
              sizeAsRef,
              setSize,
              isLoading,
              isEmpty,
              showLoadMore,
              isValidating,
            }}
            setCurrentListUrl={setCurrentListUrl}
          />
        </>
      )}
      {(topJobsFromServer?.items && topJobsFromServer?.items?.length > 0) && (
        <>
          <h2
            id="workplace-top-jobs"
            className="mb-4 mt-6 text-xl font-bold"
          >
            {t('global.label.top-jobs')}
          </h2>
          <WorkplaceJobsListInfinite
            locale={locale}
            type="top"
            jobsListData={{
              data: topJobsData,
              size: topJobsSize,
              sizeAsRef: topJobsSizeAsRef,
              setSize: topJobsSetSize,
              isLoading: topJobsIsLoading,
              isEmpty: topJobsIsEmpty,
              showLoadMore: topJobsShowLoadMore,
              isValidating: topJobsIsValidating,
            }}
            setCurrentListUrl={setCurrentListUrl}
          />
        </>
      )}
      {/* Job Detail Modal */}
      {typeof selectedJobSlugQueryParam === 'string' && selectedJobSlugQueryParam && (
        <JobListsContext.Provider value={selectedJobSlugQueryParam.split('-')?.[0] !== 'bjworkplace'
          ? jobsListsContext as never
          : topJobListsContext as never}
        >
          <Modal
            isOpen
            onClose={closeJobModal}
            size="full"
            containerClasses="overflow-x-hidden"
            className="!transform-none"
            body={(
              <JobDetailHub
                jobSlug={selectedJobSlugQueryParam}
                closeModal={closeJobModal}
                locale={locale}
              />
            )}
          />
        </JobListsContext.Provider>
      )}
    </>
  );
};
